import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';
import { type ApiError, type ApiResponse, useAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

export const CreateReportSchema = z.object({
  viewId: z.string(),
  name: z.string(),
  emails: z.array(z.string()),
  periodicity: z.object({
    type: z.union([z.literal('weekly'), z.literal('monthly')]),
    days: z.array(z.number()),
  }),
});

export type CreateReportVariables = z.infer<typeof CreateReportSchema>;

export async function createReportFn(
  axiosInstance: Promise<AxiosInstance>,
  data: CreateReportVariables,
) {
  const instance = await axiosInstance;

  return instance.post('reports/export_tasks', CreateReportSchema.parse(data));
}

export function useCreateReport() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, CreateReportVariables>({
    mutationFn: data => createReportFn(axiosInstance, data),
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['reports'],
      });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    CreateReportVariables,
    'createReport'
  >('createReport', mutation);
}
